import { useFeatureValue } from '@growthbook/growthbook-react';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useEffect, useMemo, useState } from 'react';
import { Marketplace } from './@types/api';
import { runtimeEnv } from './config/runtimeEnv';
import { APP_EVENT } from './constants/events';
import { FEATURE_FLAG } from './constants/featureFlags';
import {
  identifyOpenReplayUser,
  resetOpenReplayUser,
  startOpenReplay,
  stopOpenReplay,
  type OpenReplayFeatureConfig,
} from './utils/openReplay';
import { getItemFromSessionStorage } from './utils/storageHelper';

const DEFAULT_OPENREPLAY_FEATURE_CONFIG: OpenReplayFeatureConfig = {
  enabled: false,
  captureConsole: false,
  captureErrors: false,
  captureNetwork: false,
};

const getSelectedTenant = (): Marketplace | null => getItemFromSessionStorage('marketplace');

const OpenReplayProvider = () => {
  const featureConfig = useFeatureValue<OpenReplayFeatureConfig>(
    FEATURE_FLAG.OPENREPLAY_TRACKING,
    DEFAULT_OPENREPLAY_FEATURE_CONFIG
  );
  const { isAuthenticated, marketplaceId, userId, username } = useAuth();
  const [selectedTenant, setSelectedTenant] = useState<Marketplace | null>(getSelectedTenant);

  const metadata = useMemo(
    () => ({
      tenantId: selectedTenant?.id,
      tenantName: selectedTenant?.name,
      userId: userId ?? '',
      userMarketplaceId: marketplaceId,
      username,
    }),
    [marketplaceId, selectedTenant?.id, selectedTenant?.name, userId, username]
  );

  useEffect(() => {
    startOpenReplay({ featureConfig, runtimeEnv });

    const handlePageHide = () => {
      stopOpenReplay();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
      stopOpenReplay();
    };
  }, [featureConfig]);

  useEffect(() => {
    // Switching tenant reloads the page through RequireTenantGuard, so this listener
    // only matters for the first selection and for any future in-place switch.
    const handleTenantChange = () => {
      setSelectedTenant(getSelectedTenant());
    };

    window.addEventListener(APP_EVENT.TENANT_CHANGED, handleTenantChange);

    return () => {
      window.removeEventListener(APP_EVENT.TENANT_CHANGED, handleTenantChange);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !metadata.userId) {
      resetOpenReplayUser();
      return;
    }

    identifyOpenReplayUser(metadata);
  }, [isAuthenticated, metadata]);

  return null;
};

export default OpenReplayProvider;
